import { useState, useEffect, useRef } from "react";
import { ChevronDown, X, Check, Loader2, ArrowLeft, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import tarrantCountyZipCodes from "../../data/tarrant-county-zip-codes.json";
import { INCOME_RANGES } from "../../constants/resource-constants";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function ResourceForm({ initialData, onSubmit, isSubmitting, title }) {
  const navigate = useNavigate();

  const parseSchedule = (raw) => {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  const [formData, setFormData] = useState({
    title: initialData?.title || "",
    description: initialData?.description || "",
    contactInfo: initialData?.contactInfo || "",
    location: initialData?.location || "",
    zipCode: initialData?.zipCode || "",
    incomeRange: initialData?.incomeRange || "",
    populationServed: initialData?.populationServed || "",
  });

  const [schedule, setSchedule] = useState(parseSchedule(initialData?.schedule));

  // Categories
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState(
    initialData?.categories?.map((c) => c.id) || []
  );
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const categoryDropdownRef = useRef(null);

  // ZIP code searchable dropdown
  const [zipSearch, setZipSearch] = useState(initialData?.zipCode || "");
  const [isZipDropdownOpen, setIsZipDropdownOpen] = useState(false);
  const zipDropdownRef = useRef(null);

  // Population Served searchable dropdown
  const [populationOptions, setPopulationOptions] = useState([]);
  const [populationSearch, setPopulationSearch] = useState("");
  const [isPopulationDropdownOpen, setIsPopulationDropdownOpen] = useState(false);
  const [isLoadingPopulations, setIsLoadingPopulations] = useState(false);
  const populationDropdownRef = useRef(null);

  // Income Range dropdown
  const [isIncomeDropdownOpen, setIsIncomeDropdownOpen] = useState(false);
  const incomeDropdownRef = useRef(null);

  // Fetch categories
  useEffect(() => {
    let mounted = true;
    const fetch_ = async () => {
      setIsLoadingCategories(true);
      try {
        const res = await fetch(`${BASE_URL}${API_ENDPOINTS.GET_LOVS_PUBLIC}?key=NONPROFIT_SERVICES`);
        if (res.ok) {
          const result = await res.json();
          if (mounted) setCategoryOptions(Array.isArray(result?.data) ? result.data : []);
        }
      } finally {
        if (mounted) setIsLoadingCategories(false);
      }
    };
    fetch_();
    return () => { mounted = false; };
  }, []);

  // Fetch populations served
  useEffect(() => {
    let mounted = true;
    const fetch_ = async () => {
      setIsLoadingPopulations(true);
      try {
        const res = await fetch(`${BASE_URL}${API_ENDPOINTS.GET_LOVS_PUBLIC}?key=POPULATIONS_SERVED`);
        if (res.ok) {
          const result = await res.json();
          if (mounted) setPopulationOptions(Array.isArray(result?.data) ? result.data : []);
        }
      } finally {
        if (mounted) setIsLoadingPopulations(false);
      }
    };
    fetch_();
    return () => { mounted = false; };
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target))
        setIsCategoryDropdownOpen(false);
      if (zipDropdownRef.current && !zipDropdownRef.current.contains(e.target))
        setIsZipDropdownOpen(false);
      if (populationDropdownRef.current && !populationDropdownRef.current.contains(e.target)) {
        setIsPopulationDropdownOpen(false);
        setPopulationSearch("");
      }
      if (incomeDropdownRef.current && !incomeDropdownRef.current.contains(e.target))
        setIsIncomeDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDay = (day) => {
    setSchedule((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      ...formData,
      schedule: schedule.length > 0 ? JSON.stringify(schedule) : undefined,
      categoryIds: selectedCategories.length > 0 ? selectedCategories : undefined,
    };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
    onSubmit(payload);
  };

  const inputClass =
    "w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]";

  const filteredZips = tarrantCountyZipCodes.filter((z) => z.startsWith(zipSearch));
  const filteredPopulations = populationOptions.filter((p) =>
    p.name.toLowerCase().includes(populationSearch.toLowerCase())
  );
  const selectedPopulationName = populationOptions.find((p) => p.name === formData.populationServed)?.name || formData.populationServed;

  // Abbreviate day to 3 letters for display
  const dayAbbr = (d) => d.slice(0, 3);

  return (
    <div className="w-[90%] lg:w-[95%] mx-auto mt-8 mb-10">
      <button
        onClick={() => navigate("/dashboard/manage-resources")}
        className="flex items-center gap-1 text-black/50 hover:text-black/80 font-popinsMedium text-sm mb-6"
      >
        <ArrowLeft size={16} /> Back to Resources
      </button>

      <h1 className="font-popinsBold text-xl lg:text-2xl mb-1">{title}</h1>
      <p className="font-popinsRegular text-sm text-black/50 mb-8">Fill in the details below and save.</p>

      <form onSubmit={handleSubmit} className="lg:w-[60%] space-y-5">

        {/* Title */}
        <div>
          <label className="font-popinsMedium text-sm">Title <span className="text-red-400">*</span></label>
          <input type="text" name="title" value={formData.title} onChange={handleChange}
            placeholder="e.g. Food Assistance Program" className={inputClass} required />
        </div>

        {/* Description */}
        <div>
          <label className="font-popinsMedium text-sm">Description</label>
          <textarea name="description" value={formData.description} onChange={handleChange}
            placeholder="Brief description of the resource…" rows={3}
            className={`${inputClass} resize-none`} />
        </div>

        {/* Categories */}
        <div className="relative" ref={categoryDropdownRef}>
          <label className="font-popinsMedium text-sm">Categories</label>
          <button type="button" onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            disabled={isLoadingCategories}
            className={`w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white pl-3 pr-10 py-1 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] cursor-pointer text-left h-[42px] flex items-center relative ${selectedCategories.length === 0 ? "text-gray-400" : "text-black/50"}`}
          >
            <div className={`flex-1 flex flex-nowrap gap-1.5 items-center min-w-0 pr-3 pb-1 ${selectedCategories.length > 0 ? "overflow-x-auto" : "overflow-hidden"}`}>
              {isLoadingCategories ? (
                <span className="text-gray-400 whitespace-nowrap">Loading…</span>
              ) : selectedCategories.length === 0 ? (
                <span className="text-gray-400 whitespace-nowrap">Select categories</span>
              ) : (
                selectedCategories.map((catId) => {
                  const cat = categoryOptions.find((c) => c.id === catId);
                  if (!cat) return null;
                  return (
                    <span key={catId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-md text-xs font-popinsMedium whitespace-nowrap flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); setSelectedCategories(selectedCategories.filter((id) => id !== catId)); }}
                    >
                      {cat.name}<X size={12} className="cursor-pointer" />
                    </span>
                  );
                })
              )}
            </div>
            <ChevronDown size={20} className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isCategoryDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {isCategoryDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {categoryOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">No categories available</div>
              ) : categoryOptions.map((cat) => {
                const isSelected = selectedCategories.includes(cat.id);
                return (
                  <div key={cat.id} onClick={() => setSelectedCategories(isSelected ? selectedCategories.filter((id) => id !== cat.id) : [...selectedCategories, cat.id])}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2">
                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${isSelected ? "border-[#3FC4E3] bg-[#3FC4E3]" : "border-gray-300"}`}>
                      {isSelected && <Check size={14} className="text-white" />}
                    </div>
                    <span className="font-popinsRegular text-sm text-black/50">{cat.name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Population Served — searchable dropdown */}
        <div className="relative" ref={populationDropdownRef}>
          <label className="font-popinsMedium text-sm">Population Served</label>
          <button type="button"
            onClick={() => { setIsPopulationDropdownOpen(!isPopulationDropdownOpen); setPopulationSearch(""); }}
            className={`w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 pr-10 py-2 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] text-left relative flex items-center h-[42px] ${formData.populationServed ? "text-black/60" : "text-gray-400"}`}
          >
            <span className="flex-1 truncate">
              {selectedPopulationName || "Select population served"}
            </span>
            {formData.populationServed && (
              <X size={14} className="absolute right-8 top-1/2 -translate-y-1/2 text-black/30 hover:text-black/60 cursor-pointer z-10"
                onClick={(e) => { e.stopPropagation(); setFormData((prev) => ({ ...prev, populationServed: "" })); }} />
            )}
            <ChevronDown size={20} className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isPopulationDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {isPopulationDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg">
              {/* Search input */}
              <div className="px-3 py-2 border-b border-border flex items-center gap-2">
                <Search size={14} className="text-black/30 flex-shrink-0" />
                <input
                  type="text"
                  autoFocus
                  value={populationSearch}
                  onChange={(e) => setPopulationSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full text-sm font-popinsMedium text-black/60 outline-none bg-transparent"
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {isLoadingPopulations ? (
                  <div className="px-4 py-3 text-sm text-gray-400 font-popinsMedium">Loading…</div>
                ) : filteredPopulations.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400 font-popinsMedium">No match found</div>
                ) : filteredPopulations.map((p) => {
                  const isSelected = formData.populationServed === p.name;
                  return (
                    <div key={p.id}
                      onClick={() => { setFormData((prev) => ({ ...prev, populationServed: p.name })); setIsPopulationDropdownOpen(false); setPopulationSearch(""); }}
                      className={`px-4 py-2 text-sm font-popinsMedium cursor-pointer hover:bg-[#3FC4E3]/10 flex items-center justify-between ${isSelected ? "text-[#3FC4E3]" : "text-black/60"}`}
                    >
                      <span>{p.name}</span>
                      {isSelected && <Check size={14} className="text-[#3FC4E3] flex-shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Income Range dropdown */}
        <div className="relative" ref={incomeDropdownRef}>
          <label className="font-popinsMedium text-sm">Income Range</label>
          <button type="button" onClick={() => setIsIncomeDropdownOpen(!isIncomeDropdownOpen)}
            className={`w-full mt-1 font-popinsMedium rounded-md border-2 border-border bg-white px-4 pr-10 py-2 outline-none transition-all duration-300 ease-in-out focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3] text-left relative flex items-center h-[42px] ${formData.incomeRange ? "text-black/60" : "text-gray-400"}`}
          >
            <span>{formData.incomeRange || "Select income range"}</span>
            <ChevronDown size={20} className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isIncomeDropdownOpen ? "rotate-180" : ""}`} />
          </button>
          {isIncomeDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg">
              <div onClick={() => { setFormData((prev) => ({ ...prev, incomeRange: "" })); setIsIncomeDropdownOpen(false); }}
                className="px-4 py-2 text-sm font-popinsMedium text-gray-400 cursor-pointer hover:bg-[#3FC4E3]/10">
                None
              </div>
              {INCOME_RANGES.map((range) => (
                <div key={range}
                  onClick={() => { setFormData((prev) => ({ ...prev, incomeRange: range })); setIsIncomeDropdownOpen(false); }}
                  className={`px-4 py-2 text-sm font-popinsMedium cursor-pointer hover:bg-[#3FC4E3]/10 flex items-center justify-between ${formData.incomeRange === range ? "text-[#3FC4E3]" : "text-black/60"}`}
                >
                  {range}
                  {formData.incomeRange === range && <Check size={14} className="text-[#3FC4E3]" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location */}
        <div>
          <label className="font-popinsMedium text-sm">Location</label>
          <input type="text" name="location" value={formData.location} onChange={handleChange}
            placeholder="Address or area served" className={inputClass} />
        </div>

        {/* ZIP Code — searchable */}
        <div className="relative" ref={zipDropdownRef}>
          <label className="font-popinsMedium text-sm">ZIP Code</label>
          <input
            type="text"
            placeholder="ZIP Code"
            value={zipSearch}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              setZipSearch(val);
              setFormData((prev) => ({ ...prev, zipCode: "" }));
              setIsZipDropdownOpen(true);
            }}
            onFocus={() => setIsZipDropdownOpen(true)}
            onBlur={() => setTimeout(() => { if (!formData.zipCode) setZipSearch(""); }, 200)}
            className={`${inputClass} pr-10`}
          />
          <ChevronDown size={20} className={`text-black/50 transition-transform absolute right-3 bottom-3 pointer-events-none ${isZipDropdownOpen ? "rotate-180" : ""}`} />
          {isZipDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-52 overflow-y-auto">
              {filteredZips.length === 0 ? (
                <div className="px-4 py-2 text-sm text-black/50 font-popinsMedium">No Match Found</div>
              ) : filteredZips.map((z) => (
                <div key={z} onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { setFormData((prev) => ({ ...prev, zipCode: z })); setZipSearch(z); setIsZipDropdownOpen(false); }}
                  className="px-4 py-2 text-sm font-popinsMedium text-black/60 cursor-pointer hover:bg-[#3FC4E3]/10">
                  {z}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
        <div>
          <label className="font-popinsMedium text-sm">Contact Info</label>
          <input type="text" name="contactInfo" value={formData.contactInfo} onChange={handleChange}
            placeholder="Phone, email, or website" className={inputClass} />
        </div>

        {/* Schedule — day picker */}
        <div>
          <label className="font-popinsMedium text-sm">Schedule</label>
          <p className="font-popinsRegular text-xs text-black/40 mt-0.5 mb-2">Select the days this resource is available</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {DAYS_OF_WEEK.map((day) => {
              const active = schedule.includes(day);
              return (
                <button key={day} type="button" onClick={() => toggleDay(day)}
                  className={`px-3 py-1.5 rounded-md text-sm font-popinsMedium border-2 transition-all duration-150 ${
                    active
                      ? "bg-[#3FC4E3] border-[#3FC4E3] text-white"
                      : "bg-white border-border text-black/50 hover:border-[#3FC4E3] hover:text-[#3FC4E3]"
                  }`}
                >
                  {dayAbbr(day)}
                </button>
              );
            })}
          </div>
          {schedule.length > 0 && (
            <p className="text-xs text-black/40 font-popinsRegular mt-2">
              Selected: {schedule.join(", ")}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate("/dashboard/manage-resources")}
            className="px-5 py-2 rounded-md border border-border font-popinsMedium text-sm text-black/60 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting}
            className="flex items-center gap-2 px-5 py-2 rounded-md bg-[#3FC4E3] text-white font-popinsMedium text-sm hover:bg-[#006077] disabled:opacity-60 transition-colors">
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            Save Resource
          </button>
        </div>
      </form>
    </div>
  );
}
